import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Flex, Modal, Spin, Steps, Typography, message } from 'antd';
import { history } from 'umi';
import DrawerAddClueCard from '@/pages/ClueCenter/ClueDone/components/DrawerAddClueCard';
import DrawerOpenCard, {
  OpenCardSuccessResult,
} from '@/pages/Member/MemberDetail/components/DrawerOpenCard';
import { getMemberInfo } from '@/services/member';
import { getSourceList } from '@/services/clue';
import { getStudioList } from '@/services/studio';
import { getMemberCardList } from '@/services/member-card';
import { MemberCardInfo, MemberCardSaleStatus } from '@/types/member-card';
import { IStudioInfo } from '@/types/studio';
import {
  MemberInfo,
  OpenCardCompleteResult,
  QuickOpenCardFlowPath,
  QuickOpenCardStepKey,
  SearchUserInfo,
} from '@/types/member';
import { SourceInfo } from '@/types/clue';
import { useMerchantConfig } from '@/hooks/useMerchantConfig';
import StepSearchUser from './components/StepSearchUser';
import StepComplete from './components/StepComplete';
import { getStepPath, waitForClueUserId } from './utils';
import styles from './index.less';

const STEP_LABELS: Record<QuickOpenCardStepKey, string> = {
  search: '选择用户',
  clue: '录入线索',
  openCard: '会员开卡',
  complete: '完成',
};

const embeddedClassNames = {
  layout: styles.embeddedFixedLayout,
  scroll: styles.embeddedScroll,
  footer: styles.embeddedFooter,
};

interface IProps {
  open: boolean;
  onClose: () => void;
}

export default function DrawerQuickOpenCard({ open, onClose }: IProps) {
  const merchantConfig = useMerchantConfig();
  const [currentStep, setCurrentStep] = useState<QuickOpenCardStepKey>('search');
  const [flowPath, setFlowPath] = useState<QuickOpenCardFlowPath>('existingUser');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null);
  const [completeResult, setCompleteResult] = useState<OpenCardCompleteResult | null>(null);
  const [cluePolling, setCluePolling] = useState(false);
  const [pendingLeadId, setPendingLeadId] = useState<number>();
  const [isDirty, setIsDirty] = useState(false);

  const [studioList, setStudioList] = useState<IStudioInfo[]>([]);
  const [cardList, setCardList] = useState<MemberCardInfo[]>([]);
  const [sourceList, setSourceList] = useState<SourceInfo[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  const stepPath = useMemo(() => getStepPath(flowPath), [flowPath]);
  const currentStepIndex = stepPath.indexOf(currentStep);

  const resetState = useCallback(() => {
    setCurrentStep('search');
    setFlowPath('existingUser');
    setSearchKeyword('');
    setMemberInfo(null);
    setCompleteResult(null);
    setCluePolling(false);
    setPendingLeadId(undefined);
    setIsDirty(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty && (currentStep === 'clue' || currentStep === 'openCard')) {
      Modal.confirm({
        title: '提示',
        content: '当前填写内容尚未保存，确认关闭吗？',
        onOk: () => {
          resetState();
          onClose();
        },
      });
      return;
    }
    resetState();
    onClose();
  }, [currentStep, isDirty, onClose, resetState]);

  async function requestBaseData() {
    try {
      setDataLoading(true);
      const [studioRes, cardRes, sourceRes] = await Promise.all([
        getStudioList(),
        getMemberCardList(),
        getSourceList({
          merchant_id: Number(merchantConfig?.merchant_id),
          store_id: 1,
          page_size: 100,
        }),
      ]);
      setStudioList(studioRes?.list || []);
      setCardList(
        (cardRes || []).filter((item) => item.admin_status === MemberCardSaleStatus.NORMAL),
      );
      setSourceList(
        (sourceRes?.list || []).map((item) => ({
          source_id: item.source_id,
          source_name: item.source_name,
          label: item.source_name,
          value: item.source_id,
        })),
      );
    } catch (error) {
      console.error('加载快捷开卡数据失败', error);
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      requestBaseData();
    } else {
      resetState();
    }
  }, [open]);

  async function enterOpenCardStep(userId: number) {
    try {
      const res = await getMemberInfo(userId);
      setMemberInfo(res);
      setCurrentStep('openCard');
      setIsDirty(false);
    } catch (error) {
      message.error('获取用户信息失败');
    }
  }

  function handleSelectUser(user: SearchUserInfo) {
    setFlowPath('existingUser');
    enterOpenCardStep(user.user_id);
  }

  function handleCreateClue(keyword: string) {
    setFlowPath('newClue');
    setSearchKeyword(keyword);
    setCurrentStep('clue');
    setIsDirty(false);
  }

  async function handleClueSuccess(result: { lead_id?: number }) {
    if (!result.lead_id) {
      message.error('线索创建成功，但未获取到线索 ID');
      return;
    }

    setPendingLeadId(result.lead_id);
    setCluePolling(true);
    try {
      const userId = await waitForClueUserId(result.lead_id);
      setCluePolling(false);
      setPendingLeadId(undefined);
      await enterOpenCardStep(userId);
    } catch (error) {
      setCluePolling(false);
      message.error(error instanceof Error ? error.message : '用户生成失败');
    }
  }

  async function handleRetryPollUser() {
    if (!pendingLeadId) {
      return;
    }
    setCluePolling(true);
    try {
      const userId = await waitForClueUserId(pendingLeadId);
      setCluePolling(false);
      setPendingLeadId(undefined);
      await enterOpenCardStep(userId);
    } catch (error) {
      setCluePolling(false);
      message.error(error instanceof Error ? error.message : '用户生成失败');
    }
  }

  function handleOpenCardSuccess(result: OpenCardSuccessResult) {
    setCompleteResult(result);
    setCurrentStep('complete');
    setIsDirty(false);
  }

  function handleViewDetail() {
    if (!completeResult) {
      return;
    }
    const url =
      completeResult.payMethod === 'online' && completeResult.orderNo
        ? `/finance-management/order-detail/${completeResult.orderNo}`
        : `/member/detail/${completeResult.userId}`;
    resetState();
    onClose();
    history.push(url);
  }

  function handleBackFromClue() {
    setCurrentStep('search');
    setIsDirty(false);
  }

  function handleBackFromOpenCard() {
    setMemberInfo(null);
    setIsDirty(false);
    if (flowPath === 'newClue') {
      setCurrentStep('clue');
      return;
    }
    setCurrentStep('search');
  }

  function renderPollingOverlay(content: React.ReactNode) {
    return (
      <Flex className={styles.pollingOverlay} vertical align="center" justify="center" gap={16}>
        {content}
      </Flex>
    );
  }

  function renderLoadingState() {
    if (dataLoading && currentStep !== 'search') {
      return renderPollingOverlay(
        <>
          <Spin />
          <Typography.Text type="secondary">加载中...</Typography.Text>
        </>,
      );
    }

    if (cluePolling) {
      return renderPollingOverlay(
        <>
          <Spin size="large" />
          <Typography.Text type="secondary">线索已保存，正在生成用户…</Typography.Text>
          {pendingLeadId && (
            <Typography.Link onClick={handleRetryPollUser}>重试获取用户</Typography.Link>
          )}
        </>,
      );
    }

    return null;
  }

  function renderScrollContent() {
    switch (currentStep) {
      case 'search':
        return <StepSearchUser onSelectUser={handleSelectUser} onCreateClue={handleCreateClue} />;
      case 'complete':
        if (!completeResult) {
          return null;
        }
        return <StepComplete result={completeResult} />;
      default:
        return null;
    }
  }

  function renderEmbeddedContent() {
    if (currentStep === 'clue') {
      return (
        <div className={styles.embeddedStepWrap}>
          <Typography.Title level={4} className={styles.screenTitle}>
            录入线索
          </Typography.Title>
          <div className={styles.stepEmbeddedArea}>
            <DrawerAddClueCard
              open
              embedded
              embeddedFooterFixed
              embeddedClassNames={embeddedClassNames}
              phoneRequired
              defaultPhoneNumber={searchKeyword}
              sourceList={sourceList}
              storeList={studioList}
              submitText="保存线索并开卡"
              cancelText="上一步"
              onClose={handleBackFromClue}
              onSuccess={handleClueSuccess}
              onFormDirtyChange={setIsDirty}
            />
          </div>
        </div>
      );
    }

    if (currentStep === 'openCard' && memberInfo) {
      return (
        <div className={styles.embeddedStepWrap}>
          <Typography.Title level={4} className={styles.screenTitle}>
            会员开卡
          </Typography.Title>
          <div className={styles.stepEmbeddedArea}>
            <DrawerOpenCard
              open
              embedded
              embeddedFooterFixed
              embeddedClassNames={embeddedClassNames}
              embeddedCancelText={!stepPath.includes('clue') ? '上一步' : undefined}
              data={memberInfo}
              studioList={studioList}
              cardList={cardList}
              offlineConfirmText="确认开卡"
              onClose={handleBackFromOpenCard}
              onSuccess={handleOpenCardSuccess}
              onFormDirtyChange={setIsDirty}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  function renderBody() {
    const loadingState = renderLoadingState();
    if (loadingState) {
      return loadingState;
    }

    if (!dataLoading && !cluePolling && (currentStep === 'clue' || currentStep === 'openCard')) {
      return renderEmbeddedContent();
    }

    return <div className={styles.stepScrollArea}>{renderScrollContent()}</div>;
  }

  function renderDrawerFooter() {
    if (currentStep === 'complete' && completeResult) {
      const isOnline = completeResult.payMethod === 'online';
      return (
        <Flex justify="end">
          <Button type="primary" onClick={handleViewDetail}>
            {isOnline ? '查看订单' : '查看会员卡'}
          </Button>
        </Flex>
      );
    }
    return null;
  }

  const drawerFooter = renderDrawerFooter();

  return (
    <Drawer
      title="快捷开卡"
      width={760}
      open={open}
      onClose={handleClose}
      maskClosable={false}
      destroyOnClose
      className={styles.quickOpenCardDrawer}
      footer={drawerFooter}
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <div className={styles.stepBar}>
        <Steps
          size="small"
          current={currentStepIndex}
          className={styles.steps}
          items={stepPath.map((stepKey) => ({
            title: STEP_LABELS[stepKey],
          }))}
        />
      </div>
      <div className={styles.drawerContent}>{renderBody()}</div>
    </Drawer>
  );
}
